import { useState, Fragment, FC } from 'react'
import { ResolvedParameter, ResolvedProperty } from '../../types/functions'
import { ChevronDown, ChevronRight, Layers } from 'lucide-react'

interface FunctionParametersProps {
  parameters: ResolvedParameter[]
}

export const FunctionParameters: FC<FunctionParametersProps> = ({ parameters }) => {
  const [expandedParams, setExpandedParams] = useState<Record<string, boolean>>({})

  const toggleParamExpand = (name: string) => {
    setExpandedParams(prev => ({
      ...prev,
      [name]: !prev[name]
    }))
  }

  if (parameters.length === 0) {
    return (
      <div className="ns-params-section">
        <h3 className="ns-section-title">Parameters</h3>
        <p className="ns-empty-params">This function does not take any arguments.</p>
      </div>
    )
  }

  return (
    <div className="ns-params-section">
      <h3 className="ns-section-title">
        Parameters <span className="ns-count-badge">{parameters.length}</span>
      </h3>

      <div className="ns-table-container">
        <table className="ns-schema-table">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>Parameter</th>
              <th style={{ width: '30%' }}>Type</th>
              <th style={{ width: '45%' }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map(param => {
              const isExpanded = !!expandedParams[param.name]
              const hasSchema = !!param.resolvedInterface && param.resolvedInterface.properties.length > 0

              return (
                <Fragment key={param.name}>
                  <tr className="ns-param-row">
                    <td className="ns-param-name-cell">
                      <div className="ns-param-name-wrap">
                        <span className="ns-param-name">{param.name}</span>
                        {param.optional ? (
                          <span className="ns-flag-optional">optional</span>
                        ) : (
                          <span className="ns-flag-required">required</span>
                        )}
                      </div>
                      {param.defaultValue && (
                        <div className="ns-param-default">
                          default: <code>{param.defaultValue}</code>
                        </div>
                      )}
                    </td>

                    <td className="ns-param-type-cell">
                      <div className="ns-type-badge-wrap">
                        <span className="ns-type-badge">{param.typeString}</span>
                        {hasSchema && (
                          <button
                            type="button"
                            onClick={() => toggleParamExpand(param.name)}
                            className="ns-schema-toggle-btn"
                          >
                            <Layers size={12} />
                            <span>{isExpanded ? 'Hide Schema' : 'Schema'}</span>
                            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="ns-param-desc-cell">
                      {param.description ? (
                        <div className="ns-param-desc-text">{param.description}</div>
                      ) : (
                        <span className="ns-no-desc">—</span>
                      )}
                    </td>
                  </tr>

                  {/* Expanded Nested Interface Schema Table */}
                  {hasSchema && isExpanded && (
                    <tr className="ns-nested-schema-row">
                      <td colSpan={3} className="ns-nested-schema-cell">
                        <div className="ns-nested-schema-card">
                          <div className="ns-nested-schema-header">
                            <Layers size={14} className="ns-nested-icon" />
                            <strong>{param.resolvedInterface!.name}</strong>
                            {param.resolvedInterface!.description && (
                              <span className="ns-nested-desc">
                                — {param.resolvedInterface!.description}
                              </span>
                            )}
                          </div>

                          <NestedPropertiesTable properties={param.resolvedInterface!.properties} />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface NestedPropertiesTableProps {
  properties: ResolvedProperty[]
}

const NestedPropertiesTable: FC<NestedPropertiesTableProps> = ({ properties }) => {
  const [expandedSubProps, setExpandedSubProps] = useState<Record<string, boolean>>({})

  const toggleSubExpand = (name: string) => {
    setExpandedSubProps(prev => ({
      ...prev,
      [name]: !prev[name]
    }))
  }

  return (
    <table className="ns-nested-table">
      <thead>
        <tr>
          <th style={{ width: '28%' }}>Property</th>
          <th style={{ width: '32%' }}>Type</th>
          <th style={{ width: '40%' }}>Description</th>
        </tr>
      </thead>
      <tbody>
        {properties.map(prop => {
          const isSubExpanded = !!expandedSubProps[prop.name]
          const hasNested = !!prop.nestedProperties && prop.nestedProperties.length > 0

          return (
            <Fragment key={prop.name}>
              <tr>
                <td className="ns-prop-name-cell">
                  <span className="ns-prop-name">{prop.name}</span>
                  {prop.optional ? (
                    <span className="ns-flag-optional">optional</span>
                  ) : (
                    <span className="ns-flag-required">required</span>
                  )}
                  {prop.defaultValue && (
                    <div className="ns-param-default">
                      default: <code>{prop.defaultValue}</code>
                    </div>
                  )}
                </td>

                <td className="ns-prop-type-cell">
                  <div className="ns-type-badge-wrap">
                    <span className="ns-type-badge">{prop.typeString}</span>
                    {hasNested && (
                      <button
                        type="button"
                        onClick={() => toggleSubExpand(prop.name)}
                        className="ns-schema-toggle-btn"
                      >
                        <Layers size={12} />
                        <span>{isSubExpanded ? 'Hide' : 'Nested'}</span>
                        {isSubExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                    )}
                  </div>
                </td>

                <td className="ns-prop-desc-cell">
                  {prop.description ? (
                    <span>{prop.description}</span>
                  ) : (
                    <span className="ns-no-desc">—</span>
                  )}
                </td>
              </tr>

              {hasNested && isSubExpanded && (
                <tr className="ns-nested-schema-row">
                  <td colSpan={3} className="ns-nested-schema-cell">
                    <div className="ns-nested-schema-card">
                      <NestedPropertiesTable properties={prop.nestedProperties!} />
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}
